import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';
import { ApiGatewayService } from './api-gateway.service';
import { CheckoutMode, IPaymentMethod } from './invoicing.service';

export interface ITeyaTerminalOption {
    terminalId: string;
    terminalName: string | null;
    serialNumber: string | null;
    teyaStoreId?: string | null;
    teyaStoreName?: string | null;
    label: string;
}

export interface ITeyaConnectionStatus {
    oauthConnected: boolean;
    configured: boolean;
    active: boolean;
    teyaStoreId: string | null;
    teyaTerminalId: string | null;
    eposInstanceId: string | null;
    tokenExpiresAt: string | null;
}

export interface ITeyaPaymentStatus {
    paymentRequestId: string;
    status: string;
    progressStatus: string | null;
    statusReason: string | null;
    gatewayPaymentId: string | null;
}

export interface ITeyaCheckoutPayloadOptions {
    payLater?: boolean;
    salePayments?: { paymentMethod: string; amountCents: number; currency?: string }[];
    scheduleTerminal?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class TeyaService {
    private api = inject(ApiGatewayService);


    public teyaActive = signal(false);

    private connectionStatus$ = new BehaviorSubject<{ active: boolean }>(null);

    getConnection(storeId: number): Observable<ITeyaConnectionStatus> {
        return this.api.get(`teya/connection?storeId=${storeId}`);
    }

    listTerminals(storeId: number): Observable<{ terminals: ITeyaTerminalOption[] }> {
        return this.api.get(`teya/terminals?storeId=${storeId}`);
    }

    getConnectionStatus(storeId: number): Observable<{ active: boolean }> {
        return this.api.get(`teya/connection-status?storeId=${storeId}`).pipe(
            map((res) => {
                this.teyaActive.set(res.active);
                return res;
            }),
            tap((res) => this.connectionStatus$.next(res)),
        );
    }

    checkOrFetchConnectionStatus(storeId: number): Observable<{ active: boolean }> {
        if (this.connectionStatus$.getValue()) {
            return of(this.connectionStatus$.getValue());
        }

        return this.getConnectionStatus(storeId);
    }

    getAuthorizeUrl(storeId: number): Observable<{ authorizationUrl: string; state: string }> {
        return this.api.get(`teya/oauth/authorize-url?storeId=${storeId}`);
    }

    completeOAuth(code: string, state: string): Observable<ITeyaConnectionStatus & { storeSlug: string }> {
        return this.api.post('teya/oauth/callback', { code, state });
    }

    updateConnection(payload: {
        storeId: number;
        teyaStoreId: string;
        teyaTerminalId: string;
        eposInstanceId?: string;
    }): Observable<ITeyaConnectionStatus> {
        return this.api.put('teya/connection', payload);
    }

    disconnect(storeId: number): Observable<ITeyaConnectionStatus> {
        return this.api.delete(`teya/connection?storeId=${storeId}`).pipe(
            tap(() => this.connectionStatus$.next(null)),
        );
    }

    updateTeyaOnboarding(storeId: number, payload: unknown) {
        return this.api.post('teya/onboarding', payload);
    }

    getTeyaOnboarding(storeId: number) {
        return this.api.get(`teya/onboarding?storeId=${storeId}`);
    }


    initiateSalePayment(saleId: number, amountCents: number): Observable<{
        paymentRequestId: string;
        attemptId: number;
        status: string;
        progressStatus?: string | null;
        amountCents: number;
        currency: string;
        resumed?: boolean;
    }> {
        return this.api.post(`sales/${saleId}/teya-payment`, { amountCents });
    }

    initiateBookingPayment(
        storeId: number,
        bookingId: number,
        amountCents?: number,
    ): Observable<{
        saleId: number;
        paymentRequestId: string;
        attemptId: number;
        status: string;
        amountCents: number;
        currency: string;
    }> {
        return this.api.post(`stores/${storeId}/bookings/${bookingId}/teya-payment`, {
            ...(amountCents != null ? { amountCents } : {}),
        });
    }

    initiateSoloPayment(
        storeId: number,
        amountCents: number,
    ): Observable<{
        saleId: number;
        paymentRequestId: string;
        attemptId: number;
        status: string;
        amountCents: number;
        currency: string;
    }> {
        return this.api.post(`stores/${storeId}/teya-payment`, { amountCents });
    }

    getPaymentStatus(saleId: any, paymentRequestId: string, reconcile = true): Observable<ITeyaPaymentStatus> {
        return this.api.get(`sales/${saleId}/teya-payment/${encodeURIComponent(paymentRequestId)}/status?reconcile=${reconcile ? 'true' : 'false'}`);
    }

    cancelPayment(saleId: number, paymentRequestId: string): Observable<ITeyaPaymentStatus> {
        return this.api.post(`sales/${saleId}/teya-payment/${encodeURIComponent(paymentRequestId)}/cancel`, {});
    }
}
